import { useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import type { DecisionDelta, DecisionDeltaKind } from '../../../types/eligibilityOverlay'

export interface OverlayDeltaEditorProps {
  /** Canon decision keys under the pinned base model — used only for the client-side pre-check
   * hints (§2.3 of the design doc); the SERVER validation (`EligibilityOverlayMerger.validate`) is
   * the authority and is never duplicated here. */
  canonKeys: string[]
  added: DecisionDelta[]
  overridden: DecisionDelta[]
  removedKeys: string[]
  onChange: (next: { added: DecisionDelta[]; overridden: DecisionDelta[]; removedKeys: string[] }) => void
}

function emptyDelta(): DecisionDelta {
  return { key: '', kind: 'table', hitPolicy: 'UNIQUE', citationRef: '', rules: [] }
}

/**
 * ADR-0056 — stages `added`/`overridden` `DecisionDelta`s and `removedDecisionKeys` client-side
 * against a COPY, never against canon rows themselves (`CanonReferencePane` is the only canon
 * read, and it is non-editable). `kind:'aggregate'` is never offered — overlays author `table`/
 * `gate` decisions only (server 422 `overlay_kind_not_authorable` otherwise).
 *
 * <p>Rules/rubric are authored as raw JSON text — the same "no structured builder" posture the
 * canon `DecisionRuleView`/`CreateDecisionRuleRequest` already carries (`types/cartridgeStudio.ts`),
 * not a regression introduced here. A malformed JSON blob is caught and shown inline as a LOCAL
 * parse hint (this can never be "saved" — there is nothing valid to submit yet); it is not the
 * server's own 422 authority, which still runs against whatever well-formed payload is submitted.
 */
export function OverlayDeltaEditor({ canonKeys, added, overridden, removedKeys, onChange }: OverlayDeltaEditorProps) {
  const canonKeySet = new Set(canonKeys)

  function updateAdded(next: DecisionDelta[]) {
    onChange({ added: next, overridden, removedKeys })
  }
  function updateOverridden(next: DecisionDelta[]) {
    onChange({ added, overridden: next, removedKeys })
  }
  function updateRemoved(next: string[]) {
    onChange({ added, overridden, removedKeys: next })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-5)' }}>
      <DeltaSection
        title="Add"
        hint="A new decision key. Must NOT already exist in canon."
        rows={added}
        onAddRow={() => updateAdded([...added, emptyDelta()])}
        onRemoveRow={(i) => updateAdded(added.filter((_, idx) => idx !== i))}
        onRowChange={(i, row) => updateAdded(added.map((r, idx) => (idx === i ? row : r)))}
        keyConflictCheck={(key) => canonKeySet.has(key)}
        keyConflictMessage="This key already exists in canon — use Override, not Add, to change it."
      />

      <DeltaSection
        title="Override"
        hint="Replaces an existing canon decision WHOLESALE by key (no field-level merge)."
        rows={overridden}
        onAddRow={() => updateOverridden([...overridden, emptyDelta()])}
        onRemoveRow={(i) => updateOverridden(overridden.filter((_, idx) => idx !== i))}
        onRowChange={(i, row) => updateOverridden(overridden.map((r, idx) => (idx === i ? row : r)))}
        keyConflictCheck={(key) => key.length > 0 && !canonKeySet.has(key)}
        keyConflictMessage="This key does not exist in canon — nothing to override."
      />

      <section aria-label="Remove decision keys">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-2)' }}>
          <Icon name="trash-2" size={14} />
          <strong style={{ fontWeight: 500 }}>Remove</strong>
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
            Canon decision keys your tenant's resolution should exclude.
          </span>
        </div>
        {canonKeys.length === 0 ? (
          <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>No canon decisions to remove yet.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--iw-space-2)' }}>
            {canonKeys.map((key) => {
              const checked = removedKeys.includes(key)
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-1)' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      updateRemoved(e.target.checked ? [...removedKeys, key] : removedKeys.filter((k) => k !== key))
                    }
                  />
                  <code>{key}</code>
                </label>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

interface DeltaSectionProps {
  title: string
  hint: string
  rows: DecisionDelta[]
  onAddRow: () => void
  onRemoveRow: (index: number) => void
  onRowChange: (index: number, row: DecisionDelta) => void
  keyConflictCheck: (key: string) => boolean
  keyConflictMessage: string
}

function DeltaSection({ title, hint, rows, onAddRow, onRemoveRow, onRowChange, keyConflictCheck, keyConflictMessage }: DeltaSectionProps) {
  return (
    <section aria-label={`${title} decisions`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-2)' }}>
        <Icon name={title === 'Add' ? 'plus' : 'edit-3'} size={14} />
        <strong style={{ fontWeight: 500 }}>{title}</strong>
        <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>{hint}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="iw-btn iw-btn--sm" onClick={onAddRow}>
          <Icon name="plus" size={12} /> Add row
        </button>
      </div>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>No {title.toLowerCase()} entries staged.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
          {rows.map((row, i) => (
            <DecisionDeltaRow
              key={i}
              row={row}
              onChange={(next) => onRowChange(i, next)}
              onRemove={() => onRemoveRow(i)}
              keyConflict={row.key.length > 0 && keyConflictCheck(row.key)}
              keyConflictMessage={keyConflictMessage}
            />
          ))}
        </div>
      )}
    </section>
  )
}

interface DecisionDeltaRowProps {
  row: DecisionDelta
  onChange: (row: DecisionDelta) => void
  onRemove: () => void
  keyConflict: boolean
  keyConflictMessage: string
}

function DecisionDeltaRow({ row, onChange, onRemove, keyConflict, keyConflictMessage }: DecisionDeltaRowProps) {
  const [rulesText, setRulesText] = useState(() => JSON.stringify(row.rules ?? [], null, 2))
  const [rulesError, setRulesError] = useState<string | null>(null)
  const [rubricText, setRubricText] = useState(() => JSON.stringify(row.gate?.rubric ?? {}, null, 2))
  const [rubricError, setRubricError] = useState<string | null>(null)

  function handleKindChange(kind: DecisionDeltaKind) {
    if (kind === 'table') {
      onChange({
        key: row.key, kind, citationRef: row.citationRef ?? null,
        hitPolicy: row.hitPolicy ?? 'UNIQUE', rules: row.rules ?? [],
      })
    } else {
      onChange({
        key: row.key, kind, citationRef: row.citationRef ?? null,
        gate: row.gate ?? { prompt: '', guidance: '', rubric: {} },
      })
    }
  }

  function handleRulesTextChange(text: string) {
    setRulesText(text)
    try {
      const parsed = JSON.parse(text || '[]')
      setRulesError(null)
      onChange({ ...row, rules: parsed })
    } catch {
      setRulesError('Not valid JSON yet — this row cannot be submitted until fixed.')
    }
  }

  function handleRubricTextChange(text: string) {
    setRubricText(text)
    try {
      const parsed = JSON.parse(text || '{}')
      setRubricError(null)
      onChange({ ...row, gate: { ...(row.gate ?? { prompt: '', guidance: '' }), rubric: parsed } })
    } catch {
      setRubricError('Not valid JSON yet — this row cannot be submitted until fixed.')
    }
  }

  return (
    <div style={{ border: '1px solid var(--iw-border)', borderRadius: 'var(--iw-radius-sm)', padding: 'var(--iw-space-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Decision key</span>
          <input
            className="iw-input"
            value={row.key}
            onChange={(e) => onChange({ ...row, key: e.target.value })}
            placeholder="e.g. supplementary_state_credit"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Kind</span>
          <select className="iw-input" value={row.kind} onChange={(e) => handleKindChange(e.target.value as DecisionDeltaKind)}>
            <option value="table">table</option>
            <option value="gate">gate</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Citation ref (optional)</span>
          <input
            className="iw-input"
            value={row.citationRef ?? ''}
            onChange={(e) => onChange({ ...row, citationRef: e.target.value || null })}
          />
        </label>
        <span style={{ flex: 1 }} />
        <button type="button" className="iw-btn iw-btn--ghost iw-btn--sm" onClick={onRemove}>
          <Icon name="x" size={12} /> Remove row
        </button>
      </div>

      {keyConflict ? (
        <div style={{ marginTop: 'var(--iw-space-2)' }}>
          <InlineAlert severity="warning">{keyConflictMessage}</InlineAlert>
        </div>
      ) : null}

      {row.kind === 'table' ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)', marginTop: 'var(--iw-space-2)' }}>
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
            Hit policy
          </span>
          <input
            className="iw-input"
            value={row.hitPolicy ?? ''}
            onChange={(e) => onChange({ ...row, hitPolicy: e.target.value })}
          />
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
            Rules (JSON array of {'{conditions, conclusions, order}'})
          </span>
          <textarea
            className="iw-input"
            rows={5}
            value={rulesText}
            onChange={(e) => handleRulesTextChange(e.target.value)}
            style={{ fontFamily: 'var(--iw-font-mono)' }}
          />
          {rulesError ? <InlineAlert severity="error">{rulesError}</InlineAlert> : null}
        </label>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)', marginTop: 'var(--iw-space-2)' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
            <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Prompt</span>
            <input
              className="iw-input"
              value={row.gate?.prompt ?? ''}
              onChange={(e) => onChange({ ...row, gate: { ...(row.gate ?? { guidance: '', rubric: {} }), prompt: e.target.value } })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
            <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Guidance</span>
            <textarea
              className="iw-input"
              rows={2}
              value={row.gate?.guidance ?? ''}
              onChange={(e) => onChange({ ...row, gate: { ...(row.gate ?? { prompt: '', rubric: {} }), guidance: e.target.value } })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
            <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Evidence requirement ref (optional)</span>
            <input
              className="iw-input"
              value={row.gate?.evidenceRequirementRef ?? ''}
              onChange={(e) =>
                onChange({
                  ...row,
                  gate: { ...(row.gate ?? { prompt: '', guidance: '', rubric: {} }), evidenceRequirementRef: e.target.value || null },
                })
              }
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
            <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Rubric (JSON)</span>
            <textarea
              className="iw-input"
              rows={3}
              value={rubricText}
              onChange={(e) => handleRubricTextChange(e.target.value)}
              style={{ fontFamily: 'var(--iw-font-mono)' }}
            />
            {rubricError ? <InlineAlert severity="error">{rubricError}</InlineAlert> : null}
          </label>
        </div>
      )}
    </div>
  )
}
